import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import Pubnub, { Subscription } from 'pubnub';
import { BehaviorSubject, Observable, of, ReplaySubject, Subject, throwError } from 'rxjs';
import {
  catchError,
  delay,
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  take,
  takeUntil,
  tap,
  timeout,
} from 'rxjs/operators';
import { TcsConfigurationService } from 'src/app/core/configuration.service';
import { createSafeProps } from 'src/lib/tcs-app-insights/app-insights-util';
import { AuthenticationService } from 'src/lib/tcs-identity/authentication.service';
import { TabsService } from 'src/lib/tcs-tabs/tabs.service';
import { AppState } from 'src/store/appstate';
import {
  MessageReceived,
  PresenceChange,
  StatusChange,
} from 'src/store/pubnub/pubnub-store.actions';
import { BackpressureQueue } from 'src/util/backpressure-queue/backpressure-queue';
import { filterNull } from 'src/util/rxjs/operators/filter-null';
import { selectSubscribedChannels } from 'src/store/pubnub/pubnub-store.model';
import { retryBackoff } from 'src/util/rxjs/operators/retry-backoff';
import { networkTimeout } from 'src/util/rxjs/helper';
import { firstValueOrDefault, firstValueOrUndefined, nullOrSwitchMap } from 'src/util/rxjs';
import { TcsUserSettingsService } from 'src/lib/tcs-user-settings/tcs-user-settings.service';
import { UserViewModel } from 'src/models/person-view-model';
import { UserService } from '../../../lib/tcs-identity/user.service';
import { GraphQLMutationsService } from '../graphql/graphql-mutations.service';
import { Logger, loggerOptionsFactory, LoggerOptionsService } from '../tcs-logging/logger';
import { ConsoleLogger } from '../tcs-logging/console-logger';
import { AppInsightsLogger } from '../tcs-logging/app-insights-logger';
import { NewRelicLogger } from '../tcs-logging/new-relic-logger';
import { CacheLogger } from '../tcs-logging/cache-logger';
import { TickService } from '../tick.service';
import { PubnubConfig, toPubnubLogLevel } from './pubnub-config';
import {
  PubnubConfiguration,
  PubnubListenerParameters,
  PubnubMessageEvent,
  PubnubPresenceEvent,
  PubnubStatusEvent,
} from './pubnub-types';
import { PubnubInsightsLogger } from './loggers/pubnub-insights-logger';
import { PubnubNewRelicLogger } from './loggers/pubnub-newrelic-logger';
import { PubnubConsoleLogger } from './loggers/pubnub-console.logger';
import { PubnubCacheLogger } from './loggers/pubnub-cache-logger';
import { pubnubMonitor } from './pubnub-monitor.service';

// Before setting this to true, make sure to downgrade to pubnub v9.0.0 or disable shared worker.
// The version 10.2.8 shared worker is not backwards compatible with our
// access token. It throws the error "Illegal character at 91: ."
// when calling setAuthKey(token).
const usePubnubV1 = true;
const sharedWorkerV1Enabled = false;
const refreshTokenThresholdInSeconds = 60 * 30;

interface PubnubSubscribeError {
  errorData: {
    error: boolean;
    message: string;
    service: string;
    status: number;
    payload?: {
      channels: string[];
      error: boolean;
      service: string;
      status: number;
    };
  };
}

class PubnubEventLogger {
  private _events: { event: string; type: 'info' | 'warning' | 'error'; ignore?: boolean }[] = [
    { event: Pubnub.CATEGORIES.PNConnectedCategory, type: 'info', ignore: true },
    { event: Pubnub.CATEGORIES.PNNetworkUpCategory, type: 'info', ignore: true },
    { event: Pubnub.CATEGORIES.PNReconnectedCategory, type: 'info', ignore: false },
    { event: Pubnub.CATEGORIES.PNMalformedResponseCategory, type: 'info', ignore: true },
    { event: Pubnub.CATEGORIES.PNUnknownCategory, type: 'info', ignore: true },
    { event: Pubnub.CATEGORIES.PNSubscriptionChangedCategory, type: 'info', ignore: true },
    { event: Pubnub.CATEGORIES.PNAcknowledgmentCategory, type: 'info', ignore: true },
    { event: Pubnub.CATEGORIES.PNSharedWorkerUpdatedCategory, type: 'info', ignore: false },
    { event: Pubnub.CATEGORIES.PNDisconnectedCategory, type: 'warning', ignore: false },
    { event: Pubnub.CATEGORIES.PNNetworkIssuesCategory, type: 'warning', ignore: false },
    { event: Pubnub.CATEGORIES.PNTimeoutCategory, type: 'warning', ignore: false },
    { event: Pubnub.CATEGORIES.PNCancelledCategory, type: 'warning', ignore: false },
    {
      event: Pubnub.CATEGORIES.PNRequestMessageCountExceededCategory,
      type: 'warning',
      ignore: false,
    },
    { event: Pubnub.CATEGORIES.PNNetworkDownCategory, type: 'error', ignore: false },
    { event: Pubnub.CATEGORIES.PNBadRequestCategory, type: 'error', ignore: false },
    { event: Pubnub.CATEGORIES.PNAccessDeniedCategory, type: 'error', ignore: false },
    { event: Pubnub.CATEGORIES.PNDisconnectedUnexpectedlyCategory, type: 'error', ignore: false },
    { event: Pubnub.CATEGORIES.PNServerErrorCategory, type: 'error', ignore: false },
    { event: Pubnub.CATEGORIES.PNValidationErrorCategory, type: 'error', ignore: false },
    { event: Pubnub.CATEGORIES.PNConnectionErrorCategory, type: 'error', ignore: false },
  ];

  constructor(private _tabs: TabsService, private _user: UserService, private _logger: Logger) {}

  public logStatus(status: Pubnub.StatusEvent) {
    let msg = `PubnubEventLogger.logStatus.`;
    const message = {
      date: new Date().toISOString(),
      user: this._user.user?.email ?? 'unknown@gmr.net',
      logType: 'unknown',
      status,
    };
    const event = this._events.find((ev) => ev.event === status.category);
    const tabStatus = this._tabs.getStatus();
    if (event != null) {
      if (event.ignore !== true) {
        const isError = (status as any).error === true || event.type === 'error';
        msg += isError ? 'error' : event.type;
        message.logType = event.type;
        if (isError) {
          this._logger.error(msg, createSafeProps({ message, ...tabStatus }));
        } else if (event.type === 'info') {
          this._logger.info(msg, createSafeProps({ message, ...tabStatus }));
        } else if (event.type === 'warning') {
          this._logger.warning(msg, createSafeProps({ message, ...tabStatus }));
        }
      }
    } else {
      this._logger.warning(`${msg}unknown`, createSafeProps({ message, ...tabStatus }));
    }
  }
}

@Injectable({
  providedIn: 'root',
})
export class PubnubService implements OnDestroy {
  public client: Pubnub;
  private readonly _listener: PubnubListenerParameters;
  private _onDestroy$ = new Subject();
  private _initialized = false;

  private _subscribedChannels$ = new BehaviorSubject<string[]>([]);
  private _hasClientSyncAccess$ = new ReplaySubject<boolean>(1);
  private _messageQueue: BackpressureQueue<PubnubMessageEvent>;

  private _eventLogger: PubnubEventLogger;

  public reconnect() {
    return this.client.reconnect();
  }

  private get _config(): PubnubConfig {
    return this._tcsConfig.value.pubnub;
  }

  constructor(
    private readonly _tcsConfig: TcsConfigurationService,
    private readonly _authUserService: UserService,
    private readonly _auth: AuthenticationService,
    private readonly _store: Store<AppState>,
    private readonly _mutations: GraphQLMutationsService,
    private readonly _logger: Logger,
    private readonly _tabs: TabsService,
    private readonly _consoleLogger: ConsoleLogger,
    private readonly _insightsLogger: AppInsightsLogger,
    private readonly _newRelicLogger: NewRelicLogger,
    private readonly _cacheLogger: CacheLogger,
    private readonly _loggerSettings: LoggerOptionsService,
    private readonly _userSettings: TcsUserSettingsService,
    private readonly _tickService: TickService,
  ) {
    this._messageQueue = new BackpressureQueue(100, (message) => {
      try {
        const now = new Date();
        const msg = new MessageReceived(message, now);
        this._store.dispatch(msg);

        pubnubMonitor.recordMessageReceived(msg, now);

        if (this._userSettings.value.loggingCachePubnubMessages) {
          this._logger.info(
            'Pubnub.MessageEvent',
            { message: typeof message === 'object' ? JSON.stringify(message) : 'unknown message' },
            loggerOptionsFactory.consoleAndCacheOnly(),
          );
        }
      } catch (error: any) {
        this._logger.error('PubnubService.messageQueue error', createSafeProps({ error }));
      }
    });

    this._eventLogger = new PubnubEventLogger(this._tabs, this._authUserService, this._logger);

    this._listener = {
      message: (message: Subscription.Message) => {
        this._messageQueue.add(message as PubnubMessageEvent);
      },
      status: (status: PubnubStatusEvent) => {
        // log status
        this._eventLogger.logStatus(status);

        if (status.category === 'PNConnectedCategory') {
          const previousChannels = this._subscribedChannels$.value;
          const subscribedChannels = status.subscribedChannels ?? [];

          this._subscribedChannels$.next(subscribedChannels);

          this._logPubnubSubscribedChannels(subscribedChannels, previousChannels);
        } else if (status.category === 'PNNetworkUpCategory') {
          const channels = this._subscribedChannels$.value;
          if (channels.length > 0) {
            firstValueOrUndefined(
              this.subscribe(channels, { timeoutStrategy: 'silentlyContinue' }),
            );
            this._logger.info(
              'PubnubService.status: network up',
              createSafeProps({ status, ...this._tabs.getStatus() }),
            );
          }
        } else if (
          status.category === 'PNAccessDeniedCategory' &&
          status.operation === 'PNSubscribeOperation' &&
          (status as any).error === true
        ) {
          const error = status as any as PubnubStatusEvent & PubnubSubscribeError;
          if (error.errorData != null) {
            if (!usePubnubV1 && error.errorData.status === 403) {
              const userId = this._authUserService.user?.id;
              if (userId != null) {
                this._logger.warning(
                  'PubnubService.status: access denied error, refreshing token.',
                  createSafeProps({ error, ...this._tabs.getStatus() }),
                );
                this._refreshToken(userId, { reason: 'AccessDenied', error });
              } else {
                this._logger.error(
                  'PubnubService.status: user not found for access denied error.',
                  createSafeProps({ error, ...this._tabs.getStatus() }),
                );
              }
            } else {
              // Failed subscribing to channel(s) so we need to unsubscribe
              // in order to avoid errors when subscribing to valid channels later on.
              this._logger.error(
                'PubnubService.status: errorData on status.',
                createSafeProps({ error, ...this._tabs.getStatus() }),
              );
              if (error.errorData.payload?.channels != null) {
                firstValueOrUndefined(this._unsubscribeChannels(error.errorData.payload?.channels));
              }
            }
          }
        }

        _store.dispatch(new StatusChange(status));
        pubnubMonitor.updateStatus(status);
      },
      presence: (presence: PubnubPresenceEvent) => {
        _store.dispatch(new PresenceChange(presence));
      },
    };
  }

  public subscribe(
    channels: string[],
    options?: {
      timeoutStrategy?: 'silentlyContinue' | 'error';
      timeoutDuration?: number;
    },
  ) {
    const channelData$ = this._hasClientSyncAccess$.pipe(
      filter((isAuthorized) => isAuthorized === true),
      take(1),
      map(() => new Set(this.client.getSubscribedChannels())),
      map((subscribedChannels) => ({
        // only subscribe to channels that haven't been subscribed to yet.
        channelsToSubscribe: channels.filter((c) => !subscribedChannels.has(c)),
        currentChannels: subscribedChannels,
      })),
    );

    return channelData$.pipe(
      tap(({ channelsToSubscribe, currentChannels }) => {
        if (channelsToSubscribe.length > 0) {
          this.client.subscribe({ channels: channelsToSubscribe, withPresence: false });

          // begin subscribing to channels.
          pubnubMonitor.updateSubscribes(channelsToSubscribe, Array.from(currentChannels));

          this._logger.info(
            'PubnubService.subscribe: Subscribing',
            createSafeProps({ channels: channelsToSubscribe, ...this._tabs.getStatus() }),
          );
        }
      }),
      delay(650), // give some time for PubNub to process the subscription
      switchMap(({ channelsToSubscribe }) => {
        if (channelsToSubscribe.length === 0) {
          return of(true);
        }

        const subscribed$ = this._store.select(selectSubscribedChannels()).pipe(
          map((subscribedChannels) => {
            const subscribed = new Set(subscribedChannels);
            const notYetSubscribed = channelsToSubscribe.find(
              (channel) => !subscribed.has(channel),
            );
            const allSubscribed = notYetSubscribed == null;
            // update current subscription status
            pubnubMonitor.updateSubscribes(channelsToSubscribe, Array.from(subscribed));
            return allSubscribed;
          }),
          filter((subscribed) => subscribed),
        );

        const defaultTimeout = 10_000; // per pubnub suggestion
        const timeoutDuration = options?.timeoutDuration ?? defaultTimeout;
        if (timeoutDuration >= 0) {
          let retryCount = 0;
          const timeoutStrategy = options?.timeoutStrategy ?? 'silentlyContinue';
          return subscribed$.pipe(
            timeout(timeoutDuration),
            retryBackoff({
              maxRetries: timeoutDuration < defaultTimeout ? 5 : 3,
              initialDuration: 1000,
              canRetry: (err) => {
                retryCount++;
                this._logger.warning(
                  'PubnubService.subscribe: RetryBackoff',
                  createSafeProps({ error: err, retryCount, ...this._tabs.getStatus() }),
                );
                return true;
              },
            }),
            catchError((err) => {
              const subscribedChannels = this.client.getSubscribedChannels();
              pubnubMonitor.updateSubscribes(channelsToSubscribe, subscribedChannels, true);
              this._logger.error(
                `PubnubService.subscribe: Timeout (${timeoutDuration}ms). Timeout strategy: ${timeoutStrategy} `,
                createSafeProps({
                  timeoutDuration,
                  timeoutStrategy,
                  channels: channelsToSubscribe,
                  subscribedChannels,
                  ...this._tabs.getStatus(),
                }),
              );
              if (timeoutStrategy === 'silentlyContinue') {
                return of(true);
              }
              return throwError(err);
            }),
          );
        }
        return subscribed$;
      }),
      take(1),
    );
  }

  public unsubscribe({
    channelPrefix: prefix,
    exceptChannel: except,
  }: {
    channelPrefix: string;
    exceptChannel?: string;
  }): Observable<string[]> {
    return this._store.select(selectSubscribedChannels()).pipe(
      take(1),
      map((subscribedChannels) =>
        subscribedChannels.filter((c) => c.startsWith(prefix) && c !== except).toArray(),
      ),
      switchMap((channelsToUnsubscribe) => this._unsubscribeChannels(channelsToUnsubscribe)),
    );
  }

  private _unsubscribeChannels(channels: string[]): Observable<string[]> {
    const channelData$ = this._hasClientSyncAccess$.pipe(
      filter((isAuthorized) => isAuthorized === true),
      take(1),
      map(() => new Set(this.client.getSubscribedChannels())),
      map((subscribedChannels) => {
        // only unsubscribe from channels that are currently subscribed to.
        const channelsToUnsubscribe = channels.filter((channel) => subscribedChannels.has(channel));
        return channelsToUnsubscribe.length > 0 ? channelsToUnsubscribe : null;
      }),
    );

    return channelData$.pipe(
      tap((channelsToUnsubscribe) => {
        if (channelsToUnsubscribe != null) {
          this._unsubscribe(channelsToUnsubscribe);
          this._logger.info(
            'PubnubService.unsubscribe: Unsubscribing',
            createSafeProps({ channels: channelsToUnsubscribe, ...this._tabs.getStatus() }),
          );
        }
      }),
      nullOrSwitchMap((channelsToUnsubscribe) => {
        const unsubscribedChannels$ = this._store.select(selectSubscribedChannels()).pipe(
          map((subscribedChannels) => {
            const subscribed = new Set(subscribedChannels);
            const notYetUnsubscribed = channelsToUnsubscribe.find((channel) =>
              subscribed.has(channel),
            );
            const allUnsubscribed = notYetUnsubscribed == null;
            return allUnsubscribed ? channelsToUnsubscribe : null;
          }),
          filter((unsubscribedChannels) => unsubscribedChannels != null),
          take(1),
        );

        const timeoutDuration = 10_000;
        return unsubscribedChannels$.pipe(
          timeout(timeoutDuration),
          catchError((err) => {
            pubnubMonitor.removeChannels(channelsToUnsubscribe);
            this._logger.warning(
              `PubnubService.unsubscribe: Timeout (${timeoutDuration}ms). Removed channels without confirmation.`,
              createSafeProps({
                timeoutDuration,
                channels: channelsToUnsubscribe,
                subscribedChannels: this.client.getSubscribedChannels(),
                ...this._tabs.getStatus(),
              }),
            );
            return of(channelsToUnsubscribe);
          }),
        );
      }),
      map((unsubscribedChannels) => unsubscribedChannels ?? []),
      tap((unsubscribedChannels) => {
        if (unsubscribedChannels.length > 0) {
          const subscribedChannels = this.client.getSubscribedChannels();
          this._subscribedChannels$.next(subscribedChannels);
          this._logger.info(
            'PubnubService.unsubscribe: Unsubscribed',
            createSafeProps({
              channels: unsubscribedChannels,
              subscribedChannels,
              ...this._tabs.getStatus(),
            }),
          );
        }
      }),
      take(1),
    );
  }

  public init() {
    if (usePubnubV1) {
      this._init();
    } else {
      this._initV2();
    }
  }

  public _init() {
    if (!this._initialized) {
      this._authUserService.distinctUser$
        .pipe(
          filterNull(),
          distinctUntilChanged((u1, u2) => u1.id === u2.id),
          switchMap((user) => this._auth.getAccessToken().then((token) => ({ user, token }))),
          takeUntil(this._onDestroy$),
        )
        .subscribe(async ({ user, token }) => {
          if (token == null) {
            this._logger.error(
              'PubnubService.init: Token not found.',
              createSafeProps(this._tabs.getStatus()),
            );
            throw new Error('PubnubService.init: Token not found.');
          }

          const uuid = this._getUserIdV1(user);

          if (this.client == null) {
            this._createPubnubClient(user);
          } else if (this.client.userId !== uuid) {
            this.client.setUserId(uuid);
          }

          if (this.client.authKey !== token) {
            await this._authorizeClientSync();
            this.client.setAuthKey(token);
          }

          this._hasClientSyncAccess$.next(true);
        });

      this._initialized = true;
    }
  }

  private _createPubnubClient(user: UserViewModel) {
    const canUseSharedWorker = typeof SharedWorker !== 'undefined';
    const pnConfig: PubnubConfiguration = {
      publishKey: this._config.publishKey,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      subscribeKey: this._config.subscribeKey!,
      userId: this._getUserIdV1(user),
      //suppressLeaveEvents: true, // this should be set to false if we ever start using presence.
      requestMessageCountThreshold: 0,
      subscriptionWorkerUrl:
        sharedWorkerV1Enabled && canUseSharedWorker
          ? '/assets/pubnub/pubnub-worker-9.0.0.js'
          : undefined,
    };
    this.client = new Pubnub(pnConfig);
    this.client.addListener(this._listener);
  }

  private async _authorizeClientSync() {
    return await firstValueOrUndefined(
      this._mutations.organization
        .authorizeClientSyncAccess(
          { optionalEmail: null },
          {
            alert: { description: 'Connecting to client sync' },
          },
        )
        .pipe(take(1), timeout(networkTimeout('pubnubService.init'))),
    ).catch((error) => {
      this._logger.error(
        'PubnubService.init: Could not authorize client sync access.',
        createSafeProps({ error, ...this._tabs.getStatus() }),
      );
      throw error;
    });
  }

  private _unsubscribe(channels: string[]) {
    this.client.unsubscribe({ channels });
  }

  private _logPubnubSubscribedChannels(subscribedChannels: string[], previousChannels: string[]) {
    const hasNewChannel = subscribedChannels.find((c) => !previousChannels.includes(c)) != null;
    if (hasNewChannel || subscribedChannels.length !== previousChannels.length) {
      const tabStatus = this._tabs.getStatus();
      this._logger.info(
        'PubnubService.status subscribed',
        createSafeProps({ channels: subscribedChannels, previous: previousChannels, ...tabStatus }),
      );
    }
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }

  private _initV2(): void {
    if (!this._initialized) {
      this._authUserService.distinctUser$
        .pipe(
          filterNull(),
          distinctUntilChanged((u1, u2) => u1.id === u2.id),
          switchMap(async (user) => {
            if (this.client == null) {
              const authToken = await this._grantClientSyncAccess(user.id);
              if (authToken == null) {
                this._logger.error(
                  'PubnubService.init: Grant token failed',
                  createSafeProps(this._tabs.getStatus()),
                );
                throw new Error('PubnubService.init: Grant token failed.');
              }
              this._createPubnubClientV2(user, authToken);
            } else if (this.client.userId !== user.id) {
              const authToken = await this._grantClientSyncAccess(user.id);
              if (authToken == null) {
                this._logger.error(
                  'PubnubService.init: Grant token failed on user change',
                  createSafeProps(this._tabs.getStatus()),
                );
                throw new Error('PubnubService.init: Grant token failed on user change.');
              }
              this.client.setUserId(user.email);
              this.client.setToken(authToken);
            }
            return true;
          }),
          takeUntil(this._onDestroy$),
        )
        .subscribe(this._hasClientSyncAccess$);

      this._tickService.everyFiveMinutes$
        .pipe(
          switchMap(async () => {
            const hasAccess = await firstValueOrDefault(
              this._hasClientSyncAccess$.pipe(take(1)),
              false,
            );
            const token = hasAccess ? this.client.getToken() : undefined;
            if (token != null) {
              const { expiring, secondsToExpire } = this._isTokenExpiring(token);
              if (expiring) {
                const timestamp = new Date().toISOString();
                this._logger.warning(
                  'PubnubService: Token expiring soon, refreshing.',
                  createSafeProps({ secondsToExpire, timestamp, ...this._tabs.getStatus() }),
                );
                const userId = this._authUserService.user?.id;
                if (userId != null) {
                  return await this._refreshToken(userId, { reason: 'ExpirationCheck' });
                } else {
                  this._logger.error(
                    'PubnubService: User not found for token refresh on expiration check.',
                    createSafeProps({ ...this._tabs.getStatus() }),
                  );
                }
              }
            }
          }),
          takeUntil(this._onDestroy$),
        )
        .subscribe();

      this._initialized = true;
    }
  }

  private _getUserIdV1(user: UserViewModel): string {
    if (sharedWorkerV1Enabled) {
      // With shared worker, the userId should be the same across all tabs for PubNub to share the connection.
      return user.id;
    } else {
      // When not using shared worker, each tab must have a unique identifier.
      return `${user.id}_${this._tabs.tabId}`;
    }
  }

  private async _grantClientSyncAccess(
    userId: string,
    isRefresh: boolean = false,
  ): Promise<string | null> {
    const msg = isRefresh ? 'Refreshing client sync connection' : 'Connecting to client sync';
    const result = await firstValueOrUndefined(
      this._mutations.organization
        .grantClientSyncAccess<{ token: string }>(
          { userId },
          {
            alert: { description: msg },
          },
        )
        .pipe(take(1), timeout(networkTimeout('pubnubService._authorizeClientSyncV2'))),
    ).catch((error) => {
      this._logger.error(
        'PubnubService.init: Error while authorizing client sync access.',
        createSafeProps({ error, ...this._tabs.getStatus() }),
      );
      return null;
    });

    return result?.data == null || result.status !== 'Succeeded' ? null : result.data.token ?? null;
  }

  private _refreshToken(userId: string, logProps?: { [key: string]: any }) {
    return this._grantClientSyncAccess(userId, true).then((newToken) => {
      const props = createSafeProps({
        isTokenRefreshed: newToken != null,
        ...this._tabs.getStatus(),
        ...logProps,
      });
      if (newToken != null) {
        this.client.setToken(newToken);
        this.client.reconnect();
        this._logger.info('PubnubService.status: token refreshed.', props);
      } else {
        this._logger.error('PubnubService.status: failed to refresh token.', props);
      }
      return newToken;
    });
  }

  private _isTokenExpiring(token: string): { expiring: boolean; secondsToExpire?: number } {
    const parsedToken = this.client.parseToken(token);
    if (parsedToken == null) {
      this._logger.error(
        'PubnubService: Failed to parse token for expiration check.',
        createSafeProps({ ...this._tabs.getStatus() }),
      );
      return { expiring: false };
    }

    const now = Math.floor(Date.now() / 1000);
    const exp = parsedToken.timestamp + 60 * parsedToken.ttl;
    const secondsToExpire = exp - now;
    return { expiring: secondsToExpire < refreshTokenThresholdInSeconds, secondsToExpire };
  }

  private _createPubnubClientV2(user: UserViewModel, authKey: string) {
    const canUseSharedWorker = typeof SharedWorker !== 'undefined';
    const pnConfig: PubnubConfiguration = {
      publishKey: this._config.publishKey,
      // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
      subscribeKey: this._config.subscribeKey!,
      userId: user.email,
      suppressLeaveEvents: true, // this should be set to false if we ever start using presence.
      requestMessageCountThreshold: 0,
      subscriptionWorkerUrl: canUseSharedWorker
        ? '/assets/pubnub/pubnub-worker-10.2.8.js'
        : undefined,
      authKey,
      logLevel: toPubnubLogLevel(this._userSettings.value.pubnubLogging.level),
      loggers: [
        new PubnubInsightsLogger(this._insightsLogger, this._loggerSettings),
        new PubnubNewRelicLogger(this._newRelicLogger, this._loggerSettings),
        new PubnubConsoleLogger(this._consoleLogger, this._loggerSettings),
        new PubnubCacheLogger(this._cacheLogger, this._loggerSettings),
      ],
    };
    this.client = new Pubnub(pnConfig);
    this.client.setToken(authKey);
    this.client.addListener(this._listener);
  }
}
